<template>
  <div class="wb">
    <div class="page-head">
      <el-button :loading="loading" @click="reload">刷新</el-button>
    </div>

    <el-row :gutter="12" class="filters">
      <el-col :span="6">
        <el-select v-model="platform" style="width:100%" @change="reload">
          <el-option label="小红书" value="xiaohongshu" />
          <el-option label="抖音" value="douyin" />
        </el-select>
      </el-col>
      <el-col :span="10">
        <el-input v-model="accountId" placeholder="账号 ID" @keyup.enter="reload" />
      </el-col>
      <el-col :span="8">
        <el-select v-model="artifactType" clearable placeholder="产物类型" style="width:100%" @change="reload">
          <el-option label="全部" value="" />
          <el-option label="小红书笔记" value="xhs_note" />
          <el-option label="抖音脚本" value="douyin_script" />
          <el-option label="标题" value="titles" />
          <el-option label="封面方案" value="cover_plan" />
          <el-option label="封面实图" value="cover_image" />
          <el-option label="A/B 变体" value="ab_variant" />
          <el-option label="已选选题" value="selected_topic" />
        </el-select>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :span="14">
        <el-card shadow="never">
          <template #header>产物列表</template>
          <div v-if="!artifacts.length" class="empty">暂无产物，请先在运营 Agent 点选生成</div>
          <div
            v-for="a in artifacts"
            :key="a.id"
            class="art-item"
            :class="{ active: selected?.id === a.id }"
            @click="selected = a"
          >
            <div class="art-title">{{ a.title || a.artifact_type }}</div>
            <div class="muted">#{{ a.id }} · {{ a.artifact_type }} · {{ a.ab_group || '-' }} · {{ a.created_at }}</div>
            <img v-if="a.file_url" :src="a.file_url" class="thumb" alt="cover" />
          </div>
        </el-card>
      </el-col>
      <el-col :span="10">
        <el-card shadow="never">
          <template #header>详情 / 编辑</template>
          <div v-if="!selected" class="empty">选择左侧产物</div>
          <template v-else>
            <el-tag size="small">{{ selected.artifact_type }}</el-tag>
            <pre class="body">{{ pretty(selected.body) }}</pre>
            <el-button
              v-if="selected.topic_id || selected.body?.id || selected.body?.title"
              type="primary"
              size="small"
              @click="addToCalendar"
            >加入日历</el-button>
          </template>
        </el-card>

        <el-card shadow="never" style="margin-top:12px">
          <template #header>账号记忆</template>
          <div v-if="memory">
            <p class="muted">定位：{{ memory.account_memory?.positioning || '—' }}</p>
            <p class="muted">已做选题：{{ (memory.content_memory?.done_topics || []).length }}</p>
            <p class="muted">复盘：{{ memory.performance_summary?.avg_engagement != null ? `均互动 ${memory.performance_summary.avg_engagement}` : '尚未复盘' }}</p>
            <el-input
              v-model="prefTone"
              type="textarea"
              :rows="2"
              placeholder="偏好语气 / 备注"
              style="margin-top:8px"
            />
            <el-button size="small" style="margin-top:8px" :loading="savingMem" @click="saveMemory">保存偏好</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { contentOpsApi } from '../api'

export default {
  name: 'ContentOpsWorkbench',
  setup() {
    const platform = ref('xiaohongshu')
    const accountId = ref('')
    const artifactType = ref('')
    const artifacts = ref([])
    const selected = ref(null)
    const memory = ref(null)
    const prefTone = ref('')
    const loading = ref(false)
    const savingMem = ref(false)

    const pretty = (obj) => {
      try { return JSON.stringify(obj || {}, null, 2) } catch { return String(obj) }
    }

    const reload = async () => {
      loading.value = true
      try {
        const params = { platform: platform.value }
        if (accountId.value) params.account_id = accountId.value
        if (artifactType.value) params.artifact_type = artifactType.value
        const res = await contentOpsApi.listArtifacts(params)
        artifacts.value = res.items || []
        if (accountId.value) {
          memory.value = await contentOpsApi.getMemory({ platform: platform.value, account_id: accountId.value })
          prefTone.value = memory.value?.preferences?.tone || ''
        }
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const saveMemory = async () => {
      if (!accountId.value) { ElMessage.warning('请填写账号'); return }
      savingMem.value = true
      try {
        memory.value = await contentOpsApi.updateMemory({
          platform: platform.value,
          account_id: accountId.value,
          preferences: { tone: prefTone.value },
        })
        ElMessage.success('已保存')
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        savingMem.value = false
      }
    }

    const addToCalendar = async () => {
      if (!accountId.value || !selected.value) return
      const body = selected.value.body || {}
      const title = selected.value.title || body.title || '工作台选题'
      try {
        await contentOpsApi.createCalendar({
          platform: platform.value,
          account_id: accountId.value,
          title,
          topic_id: selected.value.topic_id || body.id,
          payload: { artifact_id: selected.value.id, body },
          status: 'draft',
        })
        ElMessage.success('已加入日历')
      } catch (e) {
        ElMessage.error(e.message || '失败')
      }
    }

    onMounted(reload)
    return {
      platform, accountId, artifactType, artifacts, selected, memory, prefTone,
      loading, savingMem, pretty, reload, saveMemory, addToCalendar,
    }
  },
}
</script>

<style scoped>
.page-head { display:flex; justify-content:flex-end; margin-bottom:12px; }
.page-title { margin:0 0 4px; font-size:18px; font-weight:750; }
.page-desc { margin:0; color:var(--mp-text-muted); font-size:12px; }
.filters { margin-bottom:12px; }
.empty { color:var(--mp-text-muted); font-size:12px; padding:16px 0; }
.art-item { border:1px solid var(--mp-border); border-radius:8px; padding:10px; margin-bottom:8px; cursor:pointer; }
.art-item.active { border-color: var(--el-color-primary); }
.art-title { font-weight:650; font-size:13px; }
.muted { color:var(--mp-text-muted); font-size:12px; margin-top:4px; }
.thumb { margin-top:8px; max-width:120px; border-radius:6px; }
.body { max-height:360px; overflow:auto; font-size:11px; background:#0f1218; color:#d7dde8; padding:10px; border-radius:8px; }
</style>
